import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FloatingMenu } from '../FloatingMenu';
import { useAssignableMembers } from '../../hooks/useRoles';
import { AssigneeMenuList } from './AssigneeMenuList';
import { AssigneeTrigger } from './AssigneeTrigger';
import { SEARCH_THRESHOLD, getAvatarColor } from './assigneeSelectorHelpers';

interface AssigneeSelectorProps {
    assignedTo: string | null;
    /** Role names whose members are offered; undefined offers everyone at ODO, [] nobody. */
    roles?: readonly string[];
    onAssign: (email: string) => void;
    onUnassign: () => void;
    currentUserEmail: string;
    dropdownAlign?: 'center' | 'right' | 'left';
    /** Prefer opening ABOVE the trigger - still collision-checked (see useFloatingPosition), so this is a hint, not an override that can run the menu off the top of the viewport. */
    dropUp?: boolean;
    /** Accessible name; needed when several selectors sit on one screen. */
    ariaLabel?: string;
}

// Measured with search box, the full max-h-60 list and Unassign; below this it shrinks to fit.
const MENU_FULL_HEIGHT = 346;

const AssigneeSelector: React.FC<AssigneeSelectorProps> = ({
    assignedTo,
    roles,
    onAssign,
    onUnassign,
    currentUserEmail,
    dropdownAlign = 'right',
    dropUp = false,
    ariaLabel
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    // Lazy: fetch only when the menu opens or the current assignee's name needs resolving.
    const { members, isLoading } = useAssignableMembers(roles, open || Boolean(assignedTo));

    const [search, setSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Reset the type-ahead whenever the menu closes.
    useEffect(() => {
        if (!open) setSearch('');
    }, [open]);

    const lastResolvedRef = useRef<{ email: string; fullname: string } | null>(null);

    const assignedMember = useMemo(() => {
        if (!assignedTo) return null;
        const found = members.find((m) => m.email === assignedTo);
        if (found) return found;
        if (lastResolvedRef.current?.email === assignedTo) {
            return { ...lastResolvedRef.current, id: '' };
        }
        return { email: assignedTo, fullname: assignedTo.split('@')[0], id: '' };
    }, [assignedTo, members]);

    // Cache resolved member outside of useMemo to avoid side effects during render
    useEffect(() => {
        if (!assignedTo) {
            lastResolvedRef.current = null;
        } else {
            const found = members.find((m) => m.email === assignedTo);
            if (found) {
                lastResolvedRef.current = { email: found.email, fullname: found.fullname };
            }
        }
    }, [assignedTo, members]);

    const isMe = assignedTo === currentUserEmail;

    const sortedMembers = useMemo(() => {
        return [...members].sort((a, b) => {
            if (a.email === currentUserEmail) return -1;
            if (b.email === currentUserEmail) return 1;
            return a.fullname.localeCompare(b.fullname);
        });
    }, [members, currentUserEmail]);

    const showSearch = open && !isLoading && sortedMembers.length > SEARCH_THRESHOLD;

    // Plain `autoFocus` would scroll any scrollable ancestor to wherever the
    // browser thinks the input now sits, reading as the page jumping.
    // `{ preventScroll: true }` keeps the focus without the scroll.
    useEffect(() => {
        if (showSearch) searchInputRef.current?.focus({ preventScroll: true });
    }, [showSearch]);

    const filteredMembers = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return sortedMembers;
        return sortedMembers.filter(
            (m) => m.fullname.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
        );
    }, [sortedMembers, search]);

    const assigneeName = assignedMember?.fullname ?? '';
    const assigneeColor = getAvatarColor(assigneeName);

    const handleAssign = (email: string) => {
        onAssign(email);
        setOpen(false);
    };

    const handleUnassign = () => {
        onUnassign();
        setOpen(false);
    };

    return (
        <div ref={ref} className="relative min-w-0">
            <AssigneeTrigger
                assignedTo={assignedTo}
                isMe={isMe}
                assigneeName={assigneeName}
                assigneeColor={assigneeColor}
                ariaLabel={ariaLabel}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                }}
            />

            {/* Portaled to <body> and positioned off the trigger's own rect -
                an absolutely-positioned menu inside a scroll container (e.g.
                the accounts rail, or this table's scroll body) can be
                clipped AND unreachable by scrolling at the same time, since
                it never contributes to that ancestor's scroll height. See
                FloatingMenu.tsx / useFloatingPosition.ts. */}
            <FloatingMenu
                open={open}
                triggerRef={ref}
                onClose={() => setOpen(false)}
                align={dropdownAlign}
                preferUp={dropUp}
                estimatedHeight={MENU_FULL_HEIGHT}
            >
                <div
                    className="flex flex-col max-h-[inherit] bg-white rounded-xl shadow-xl border border-gray-100 p-3 min-w-[220px] animate-[fadeSlideDown_120ms_ease-out]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <AssigneeMenuList
                        isLoading={isLoading}
                        sortedMembers={sortedMembers}
                        filteredMembers={filteredMembers}
                        showSearch={showSearch}
                        search={search}
                        onSearchChange={setSearch}
                        searchInputRef={searchInputRef}
                        assignedTo={assignedTo}
                        currentUserEmail={currentUserEmail}
                        onAssign={handleAssign}
                        onUnassign={handleUnassign}
                    />
                </div>
            </FloatingMenu>
        </div>
    );
};

export default AssigneeSelector;
